import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Feature, LineString, MultiLineString, Position } from "geojson";
import * as Yup from "yup";

import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TextField as MuiTextField,
  TableRow,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import { Delete, Edit, Search } from "@mui/icons-material";
import useAppSelector from "../../hooks/useAppSelector";
import useAppDispatch from "../../hooks/useAppDispatch";
import {
  createRoad,
  deleteRoad,
  fetchAllRoads,
  fetchRoads,
  openRoadForm,
  setRoad,
  updateRoad,
} from "../../redux/slices/road";
import { Formik } from "formik";
import useAuth from "../../hooks/useAuth";

import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import { WGS84lalToETRSTM35FINxy } from "../../utils/coordinateConverter";
import { geometryToWsg84 } from "../../utils/road";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";
import { a11yProps } from "./CustomTabs";
import { validateRoad } from "./Association";
import MapZoomControls from "./mapComponents/MapZoomControl";
import { MapComponent } from "./mapComponents/MapComponent";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const Button = styled(MuiButton)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY || "";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Nimi on pakollinen"),
  municipality: Yup.string().required("Kunta on pakollinen"),
});

function Mappi(props: any) {
  const mapDiv = useRef<HTMLDivElement>(null);
  const { currentUser } = useAuth();
  const [selectedRoadId, setSelectedRoadId] = useState<string | number | null>(
    null
  );
  const dispatch = useAppDispatch();
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [lng, setLng] = useState(0);
  const [lat, setLat] = useState(0);
  const [currentCoordinates, setCurrentCoordinates] = useState<
    Position | Position[] | Position[][] | Position[][][]
  >([]);
  const [basemap, setBasemap] = useState<
    "mapbox" | "mml-maastokartta" | "mml-taustakartta" | "mml-ortokuva"
  >("mapbox");
  const currentCoordinatesRef = useRef<any>(null);
  const selectedRoadIdRef = useRef<string | number | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);

  const sourceRoad = props.road || props.roads?.[0];

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const setLayerVisible = (layerId: string, visible: boolean) => {
    if (!map || !map.getLayer(layerId)) return;
    map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
  };

  const handleZoomIn = () => {
    if (map) {
      map.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (map) {
      map.zoomOut();
    }
  };

  useEffect(() => {
    if (sourceRoad && sourceRoad.location?.coordinates) {
      setLng(sourceRoad.location.coordinates[0]);
      setLat(sourceRoad.location.coordinates[1]);
    }
  }, [sourceRoad]);

  useEffect(() => {
    currentCoordinatesRef.current = currentCoordinates;
  }, [currentCoordinates]);

  useEffect(() => {
    const attachMap = (
      setMap: React.Dispatch<React.SetStateAction<mapboxgl.Map | null>>,
      mapDiv: React.RefObject<HTMLDivElement>
    ) => {
      if (!mapDiv.current) return;
      if (mapDiv.current.childNodes.length > 0) {
        mapDiv.current.innerHTML = "";
      }

      const map = new mapboxgl.Map({
        container: mapDiv.current,
        style: "mapbox://styles/mapbox/outdoors-v11",
        center: [lng, lat],
        zoom: 11,
      });

      function getNearestLine(
        coordinates: number[][][],
        clickPoint: mapboxgl.LngLat
      ): number[][] {
        let closest = coordinates[0];
        let minDistance = Infinity;

        coordinates.forEach((lineCoords) => {
          lineCoords.forEach(([lon, lat]) => {
            const dx = clickPoint.lng - lon;
            const dy = clickPoint.lat - lat;
            const dist = dx * dx + dy * dy;

            if (dist < minDistance) {
              minDistance = dist;
              closest = lineCoords;
            }
          });
        });

        return closest;
      }

      map.on("load", () => {
        map.addSource("mml-taustakartta", {
          type: "raster",
          tiles: [
            "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/taustakartta/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
          ],
          tileSize: 256,
          attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
        });

        map.addLayer({
          id: "mml-taustakartta",
          type: "raster",
          source: "mml-taustakartta",
          paint: { "raster-opacity": 1 },
        });

        map.addSource("mml-ortokuva", {
          type: "raster",
          tiles: [
            "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/ortokuva/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
          ],
          tileSize: 256,
          attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
        });

        map.addLayer({
          id: "mml-ortokuva",
          type: "raster",
          source: "mml-ortokuva",
          paint: { "raster-opacity": 1 },
        });

        map.addSource("mml-maastokartta", {
          type: "raster",
          tiles: [
            "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/maastokartta/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=9396425f-0fe1-4631-adc9-a3f6621fcbcb",
          ],
          tileSize: 256,
          attribution: "© Maanmittauslaitos (NLS) – Maastokartta, CC BY 4.0",
        });

        map.addLayer({
          id: "mml-maastokartta",
          type: "raster",
          source: "mml-maastokartta",
          paint: { "raster-opacity": 1 },
        });

        map.addSource("highlighted-road", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });

        map.addLayer({
          id: "highlighted-road",
          type: "line",
          source: "highlighted-road",
          paint: {
            "line-color": "#ffa500",
            "line-width": 4,
          },
        });

        if (!props.road) {
          map.addLayer({
            id: "custom-roads-clickzone",
            type: "line",
            source: {
              type: "vector",
              url: "mapbox://mapbox.mapbox-streets-v8",
            },
            "source-layer": "road",
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "transparent",
              "line-width": 15,
            },
          });
        }

        map.addSource("location", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: props.roads.map((road: any) => ({
              type: "Feature",
              properties: {},
              geometry: road.location,
            })),
          },
        });

        map.addLayer({
          id: "location",
          type: "circle",
          source: "location",
          paint: {
            "circle-color": "red",
          },
        });

        map.addSource("routes", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: props.roads.map((road: any) => ({
              type: "Feature",
              properties: {},
              geometry: road.geom,
            })),
          },
        });

        map.addLayer({
          id: "routes",
          type: "line",
          source: "routes",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "blue",
            "line-width": 2,
          },
        });

        // 🔧 Only highlight logic changes below
        if (!props.road) {
          map.on("click", async (e) => {
            const features = map.queryRenderedFeatures(e.point, {
              layers: ["custom-roads-clickzone"],
            });

            if (!features.length) {
              setSelectedRoadId(null);
              selectedRoadIdRef.current = null;
              setCurrentCoordinates([]);
              map.getCanvas().style.cursor = "";
              const popups = document.getElementsByClassName("mapboxgl-popup");
              while (popups.length) popups[0].remove();
              return;
            }

            const feature = features[0];
            const clickedFeatureId = String(
              feature.id || JSON.stringify(feature.geometry)
            );

            if (selectedRoadIdRef.current === clickedFeatureId) {
              setSelectedRoadId(null);
              selectedRoadIdRef.current = null;
              setCurrentCoordinates([]);
              (
                map.getSource("highlighted-road") as mapboxgl.GeoJSONSource
              ).setData({
                type: "FeatureCollection",
                features: [],
              });
              return;
            }

            selectedRoadIdRef.current = clickedFeatureId;
            setSelectedRoadId(clickedFeatureId);

            let coordinates: number[][] | undefined;

            if (
              Array.isArray(currentCoordinatesRef.current) &&
              currentCoordinatesRef.current.length > 0
            ) {
              coordinates = currentCoordinatesRef.current as number[][];
            } else {
              const geometryType = feature.geometry.type;
              if (geometryType === "LineString") {
                coordinates = (feature.geometry as LineString).coordinates;
              } else if (geometryType === "MultiLineString") {
                coordinates = getNearestLine(
                  (feature.geometry as MultiLineString).coordinates,
                  e.lngLat
                );
              } else {
                console.warn("Tuntematon geometria:", geometryType);
                return;
              }
            }

            const newHighlight: Feature<LineString> = {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: coordinates,
              },
              properties: feature.properties || {},
            };
            (
              map.getSource("highlighted-road") as mapboxgl.GeoJSONSource
            ).setData(newHighlight);

            const clickedLngLat = e.lngLat;
            const updatedCoordinates = WGS84lalToETRSTM35FINxy({
              Lo: clickedLngLat.lng,
              La: clickedLngLat.lat,
            });

            const fetchTransform = async () => {
              const url = `https://avoinapi.vaylapilvi.fi/viitekehysmuunnin/muunna?x=${updatedCoordinates.E}&y=${updatedCoordinates.N}&valihaku=false&palautusarvot=1&palautusarvot=2&palautusarvot=3&palautusarvot=4&palautusarvot=5&palautusarvot=6`;
              try {
                const res = await fetch(url);
                return res.ok ? await res.json() : null;
              } catch (err) {
                console.error("API-virhe:", err);
                return null;
              }
            };

            const fetchCoordinates = async (data: any) => {
              const props = data?.features?.[0]?.properties;
              if (!props?.katunimi || !props?.kuntanimi) return;
              const url = `https://avoinapi.vaylapilvi.fi/viitekehysmuunnin/muunna?kuntanimi=${props.kuntanimi}&katunimi=${props.katunimi}&valihaku=true&palautusarvot=1&palautusarvot=2&palautusarvot=3&palautusarvot=4&palautusarvot=5&palautusarvot=6`;
              try {
                const res = await fetch(url);
                return res.ok ? await res.json() : null;
              } catch (err) {
                console.error("API-virhe:", err);
                return null;
              }
            };

            const transformData = await fetchTransform();
            if (!transformData) return;

            const tienimi =
              transformData.features?.[0]?.properties?.katunimi ??
              "Nimetön tie";
            const kuntanimi =
              transformData.features?.[0]?.properties?.kuntanimi ??
              "Tuntematon";
            const newRoadData = {
              name: tienimi,
              municipality: kuntanimi,
              parent:
                currentUser?.activeAssociation.road.id ??
                currentUser?.associations[0].road.id,
            };

            dispatch(setRoad(newRoadData));

            const coords = await fetchCoordinates(transformData);
            if (coords?.features?.length > 0) {
              const feature = coords.features[0];
              const modifiedCoordinates = geometryToWsg84(feature);
              const flattened: number[][] = Array.isArray(
                modifiedCoordinates[0]
              )
                ? (modifiedCoordinates as number[][][]).flat()
                : (modifiedCoordinates as number[][]);

              setCurrentCoordinates(flattened);

              const newData: Feature<LineString> = {
                type: "Feature",
                geometry: {
                  type: "LineString",
                  coordinates: flattened,
                },
                properties: feature.properties || {},
              };

              (
                map.getSource("highlighted-road") as mapboxgl.GeoJSONSource
              ).setData(newData);
            } else {
              setCurrentCoordinates([]);
            }

            const existsAlready = props.roads.some(
              (road: any) => road.name === tienimi
            );
            const isUnknownRoad = newRoadData.name === "Nimetön tie";

            let popupContent = `
              <strong>Tie:</strong> ${tienimi}<br/>
              <strong>Kunta:</strong> ${kuntanimi}<br/>
            `;
            if (!existsAlready && !isUnknownRoad) {
              popupContent += `<button class="add-road-button" type="button">Lisää tie järjestelmään</button>`;
            }

            popupRef.current?.remove();

            const popup = new mapboxgl.Popup()
              .setLngLat(clickedLngLat)
              .setHTML(popupContent)
              .addTo(map);

            popupRef.current = popup;

            popup.on("close", () => {
              if (map.getSource("highlighted-road")) {
                const source = map.getSource(
                  "highlighted-road"
                ) as mapboxgl.GeoJSONSource;
                source.setData({
                  type: "FeatureCollection",
                  features: [],
                });
              }

              setSelectedRoadId(null);
              selectedRoadIdRef.current = null;
              setCurrentCoordinates([]);

              popupRef.current = null;
            });

            // Wait for DOM to attach and set button handler
            setTimeout(() => {
              const button = document.querySelector(".add-road-button");
              if (button instanceof HTMLButtonElement) {
                button.onclick = () => props.handleSubmit(newRoadData);
              }
            }, 50);
          });

          map.on("mousemove", (e) => {
            const features = map.queryRenderedFeatures(e.point, {
              layers: ["custom-roads-clickzone"],
            });
            map.getCanvas().style.cursor = features.length ? "pointer" : "";
          });
        }

        setMap(map);
      });
    };

    if (!map) attachMap(setMap, mapDiv);
  }, [
    map,
    lat,
    lng,
    props.roads,
    props,
    selectedRoadId,
    currentUser?.associations,
    currentCoordinates,
  ]);

  useEffect(() => {
    if (!map) return;

    // hide all MML layers by default
    const mmlLayers = ["mml-maastokartta", "mml-taustakartta", "mml-ortokuva"];
    mmlLayers.forEach((id) => setLayerVisible(id, false));

    // show the chosen one
    if (basemap !== "mapbox") {
      setLayerVisible(basemap, true);
    }
  }, [map, basemap]);

  return (
    <Card mb={6} style={{ position: "relative" }}>
      <div
        style={{ width: "100%", height: "650px", flexGrow: 1 }}
        ref={mapDiv}
        className="map-container"
      />
      <MapZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
    </Card>
  );
}

function RoadForm({ onComplete }: { onComplete: (success: boolean) => void }) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const [value, setValue] = useState(0);
  const { road, roads, allRoads } = useAppSelector((state) => state.road);
  const [currentRoad, setCurrentRoad] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState<{
    currentRoad: string;
    municipality: string;
  }>({
    currentRoad: "",
    municipality: "",
  });
  const [validationError, setValidationError] = useState<string | undefined>();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  useEffect(() => {
    if (associationId) {
      dispatch(fetchAllRoads());
      dispatch(fetchRoads(associationId));
    }
  }, [dispatch, associationId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery({ currentRoad, municipality });
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [currentRoad, municipality]);

  useEffect(() => {
    async function validate() {
      const {
        currentRoad: debouncedRoad,
        municipality: debouncedMunicipality,
      } = debouncedQuery;

      if (
        debouncedRoad &&
        debouncedRoad.length >= 3 &&
        debouncedMunicipality &&
        debouncedMunicipality.trim().length > 0
      ) {
        const error = await validateRoad(
          debouncedRoad,
          debouncedMunicipality,
          allRoads
        );
        setValidationError(error);
      } else {
        setValidationError(undefined);
      }
    }

    validate();
  }, [debouncedQuery]);

  const handleSubmit = async (
    values: any,
    { resetForm, setErrors, setStatus, setSubmitting }: any
  ) => {
    try {
      if (road && road.id !== undefined) {
        const nameOfVeryFirstRoad = roads[0].name;
        const parentId =
          currentUser?.activeAssociation.road.id ??
          currentUser?.associations[0].road.id;

        const sameAsFirstRoad = nameOfVeryFirstRoad === road.name;

        const finalParentId = sameAsFirstRoad ? null : parentId;

        const updatedValues = {
          ...values,
          parent: finalParentId,
        };

        await dispatch(updateRoad(road.id, updatedValues));
      } else {
        await dispatch(createRoad(values));
      }

      await dispatch(fetchRoads(associationId));

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      dispatch(openRoadForm(false));
      onComplete(true);
    } catch (error) {
      setStatus({ sent: false });
      setErrors({ submit: "Error" });
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={
        road
          ? {
              name: road.name,
              municipality: road.municipality,
              parent:
                currentUser?.activeAssociation.road.id ??
                currentUser?.associations[0].road.id,
            }
          : {
              name: "",
              municipality: "",
              parent:
                currentUser?.activeAssociation.road.id ??
                currentUser?.associations[0].road.id,
            }
      }
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <Card mb={6}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tiet
            </Typography>
            <Box sx={{ borderBottom: 1, mb: 3, borderColor: "divider" }}>
              <Tabs
                value={value}
                onChange={handleTabChange}
                aria-label="basic tabs example"
              >
                <Tab
                  label={
                    road === undefined ? "Lisää lomakkeella" : "Muokkaa tietä"
                  }
                  {...a11yProps(0)}
                />
                <Tab
                  label={
                    road === undefined ? "Lisää kartasta" : "Näytä kartalla"
                  }
                  {...a11yProps(1)}
                />
              </Tabs>
            </Box>

            {value === 0 && (
              <>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={6}>
                    <Grid item md={6}>
                      <TextField
                        name="name"
                        label="Nimi"
                        value={values.name}
                        error={Boolean(validationError)}
                        fullWidth
                        helperText={validationError}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleChange(e);
                          setCurrentRoad(e.target.value);
                        }}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                    <Grid item md={6}>
                      <TextField
                        name="municipality"
                        label="Kunta"
                        value={values.municipality}
                        error={Boolean(validationError)}
                        fullWidth
                        helperText={validationError}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleChange(e);
                          setMunicipality(e.target.value);
                        }}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                  </Grid>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    mt={3}
                    mr={2}
                  >
                    Tallenna
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    mt={3}
                    onClick={() => dispatch(openRoadForm(false))}
                  >
                    Peruuta
                  </Button>
                </form>
              </>
            )}
            {value === 1 &&
              (road !== undefined ? (
                <>
                  <Typography variant="h6" gutterBottom mt={3} mb={3}>
                    Kartta
                  </Typography>
                  <MapComponent
                    roads={[road]}
                    road={road}
                    properties={[]}
                    type={"roads"}
                    handleSubmit={handleSubmit}
                  />
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom mt={3} mb={3}>
                    Kartta
                  </Typography>
                  <MapComponent
                    roads={roads}
                    road={road}
                    properties={[]}
                    type={"roads"}
                    handleSubmit={handleSubmit}
                  />
                </>
              ))}
          </CardContent>
        </Card>
      )}
    </Formik>
  );
}

function RoadList(props: any) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();

  const { status = "idle" } = useAppSelector((state) => state.road || {});

  useEffect(() => {
    const associationId =
      currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;
    if (associationId) {
      dispatch(fetchRoads(associationId));
    }
  }, [dispatch, currentUser]);

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tiet
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size={"small"}
          onClick={() => props.handleEditUser(undefined, true)}
        >
          Lisää
        </Button>
      </CardContent>

      <Paper>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell>Nimi</TableCell>
              <TableCell>Kunta</TableCell>
              <TableCell>Maakunta</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              (props.rows || []).map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.municipality}</TableCell>
                  <TableCell>{row.county}</TableCell>
                  <TableCell>
                    <Search
                      style={{ cursor: "pointer" }}
                      onClick={() => props.handleShowRoad(row, true)}
                    />{" "}
                    <Edit
                      style={{ cursor: "pointer" }}
                      onClick={() => props.handleEditUser(row, true)}
                    />{" "}
                    {currentUser?.role === "SUPERADMIN" ? (
                      <Delete
                        style={{ cursor: "pointer" }}
                        onClick={() => props.handleDelete(row)}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Roads() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { roads, open } = useAppSelector((state) => state.road);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleShowRoad = (road: any, isOpen: boolean) => {
    dispatch(setRoad(road));
    dispatch(openRoadForm(isOpen));
  };

  const handleEditRoad = async (road: any, isOpen: boolean) => {
    dispatch(setRoad(road));
    dispatch(openRoadForm(isOpen));
  };

  const handleDelete = async (road: any) => {
    const confirmed = window.confirm(
      `Haluatko varmasti poistaa "${road.name}"?`
    );
    if (confirmed) {
      setProgress(100);
      try {
        await dispatch(deleteRoad(road.id));
        setSnackbarMessage("Tie poistettu onnistuneesti!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (e) {
        setSnackbarMessage("Virhe poistettaessa tietä.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  useEffect(() => {
    dispatch(fetchRoads(associationId));

    return () => {
      dispatch(openRoadForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Tiet" />
      <Typography variant="h3" gutterBottom display="inline">
        Tiet
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Tiet</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {open ? (
        <RoadForm
          onComplete={(success: any) => {
            setProgress(100);
            if (success) {
              setSnackbarMessage("Tie tallennettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              setSnackbarMessage("Virhe tallennettaessa tietä.");
              setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
          }}
        />
      ) : (
        <RoadList
          rows={roads}
          handleShowRoad={handleShowRoad}
          handleEditUser={handleEditRoad}
          handleDelete={handleDelete}
          openForm={open}
        />
      )}
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Roads;
